import { motion, AnimatePresence } from "framer-motion";
import { Mail, FolderOpen, HardDrive, Plug, Check, ShieldCheck, Sparkles } from "lucide-react";
import { useT } from "@/i18n/useLocale";
import { ERP_LOGOS } from "./HeroFacturas";

const SOURCE_ICONS = [Mail, Mail, HardDrive, FolderOpen, Plug];

const fade = {
  initial: { opacity: 0, scale: 0.96, y: 12 },
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.98, y: -8 },
  transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] as const },
};

export function FlujoVisual({ step }: { step: number }) {
  const t = useT();
  const v = t.facturas.flujo.visual;

  return (
    <div className="relative aspect-[2/3] w-full overflow-hidden rounded-3xl border border-hairline bg-surface p-6 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.6)] sm:aspect-[4/3] lg:p-8">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-16 -left-16 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute -bottom-16 -right-10 h-56 w-56 rounded-full bg-accent/15 blur-3xl" />
      </div>

      <div className="relative z-20 flex items-center justify-between">
        <span className="rounded-full border border-accent/50 bg-background/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-accent backdrop-blur">
          {t.facturas.flujo.stepLabel} 0{step + 1}
        </span>
        <span className="text-[11px] uppercase tracking-[0.14em] text-ink-soft/70">
          {[v.step1, v.step2, v.step3, v.step4, v.step5][step] ?? v.step1}
        </span>
      </div>

      <div className="relative z-10 mt-5 flex h-[calc(100%-2.75rem)] w-full items-center justify-center">
        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div key="s0" {...fade} className="flex flex-wrap items-center justify-center gap-3">
              {v.sources.map((s, i) => {
                const Icon = SOURCE_ICONS[i] ?? Mail;
                return (
                  <motion.div
                    key={s}
                    animate={{ y: [0, -8, 0] }}
                    transition={{ duration: 3 + i * 0.4, repeat: Infinity, ease: "easeInOut" }}
                    className="flex items-center gap-2 rounded-2xl border border-hairline bg-background px-4 py-3 shadow-sm"
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="text-sm font-medium text-ink">{s}</span>
                  </motion.div>
                );
              })}
            </motion.div>
          )}

          {(step === 1 || step === 2) && (
            <motion.div key="s12" {...fade} className="relative w-full max-w-sm">
              <div className="rounded-2xl border border-hairline bg-background p-5 shadow-sm">
                <div className="flex items-start justify-between border-b border-hairline pb-3">
                  <div>
                    <p className="font-display text-sm tracking-tight text-ink">{v.invoiceTitle}</p>
                    <p className="mt-1 text-xs text-ink-soft">{v.invoiceSupplier}</p>
                    <span
                      className={`mt-2 inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] transition-colors ${
                        step === 2
                          ? "border-primary/40 bg-primary/[0.07] text-primary"
                          : "border-hairline text-ink-soft"
                      }`}
                    >
                      {v.ocLabel} · {v.ocValue}
                    </span>
                  </div>
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent/15 text-accent">
                    <Sparkles className="h-4 w-4" />
                  </span>
                </div>

                <div className="mt-3 space-y-2">
                  {v.fields.map((f, i) => (
                    <motion.div
                      key={f.k}
                      animate={
                        step === 2
                          ? { borderColor: "rgba(30,75,142,0.45)", backgroundColor: "rgba(30,75,142,0.05)" }
                          : { borderColor: "rgba(0,0,0,0)", backgroundColor: "rgba(0,0,0,0)" }
                      }
                      transition={{ duration: 0.4, delay: step === 2 ? i * 0.12 : 0 }}
                      className="flex items-center justify-between gap-3 rounded-lg border border-transparent px-2 py-1.5 text-xs"
                    >
                      <span className="text-ink-soft">{f.k}</span>
                      <span className="font-medium text-ink">{f.v}</span>
                    </motion.div>
                  ))}
                </div>

                <AnimatePresence>
                  {step === 2 && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.35, delay: 0.4 }}
                      className="overflow-hidden"
                    >
                      <div className="mt-3 flex flex-wrap items-center gap-1.5 rounded-xl border border-accent/30 bg-accent/[0.06] px-3 py-2">
                        <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-soft">
                          {v.assignLabel}
                        </span>
                        <span className="rounded-full border border-hairline bg-background px-2 py-0.5 text-[10px] font-medium text-ink">
                          {v.accounting}
                        </span>
                        <span className="rounded-full border border-hairline bg-background px-2 py-0.5 text-[10px] font-medium text-ink">
                          {v.mapped}
                        </span>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <AnimatePresence>
                {step === 2 && (
                  <>
                    <motion.span
                      initial={{ opacity: 0, y: 10, scale: 0.94 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ delay: 0.15, type: "spring", stiffness: 210, damping: 18 }}
                      className="absolute -top-4 -right-2 flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary px-3 py-1 text-[10px] font-semibold text-primary-foreground shadow-lg sm:-right-3 sm:px-4 sm:py-1.5 sm:text-[11px]"
                    >
                      <ShieldCheck className="h-3.5 w-3.5" />
                      {v.matchOk}
                      <span className="hidden sm:inline">· {v.ocValue}</span>
                    </motion.span>
                  </>
                )}
              </AnimatePresence>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div key="s3" {...fade} className="w-full max-w-sm">
              <div className="overflow-hidden rounded-2xl border border-hairline bg-background shadow-sm">
                <div className="flex items-center gap-2 border-b border-hairline bg-surface px-4 py-3">
                  <span className="h-2 w-2 rounded-full bg-accent" />
                  <p className="text-xs font-medium uppercase tracking-[0.12em] text-ink-soft">
                    {v.approvalTitle}
                  </p>
                </div>
                <div className="p-5">
                  <p className="text-sm leading-relaxed text-ink">{v.approvalBody}</p>
                  <div className="mt-3 flex items-center justify-between rounded-lg bg-surface px-3 py-2 text-xs">
                    <span className="text-ink-soft">{v.invoiceSupplier}</span>
                    <span className="font-medium text-ink">{v.fields[3]?.v}</span>
                  </div>
                  <div className="mt-5 flex gap-2">
                    <motion.span
                      animate={{ scale: [1, 1.04, 1] }}
                      transition={{ duration: 1.8, repeat: Infinity }}
                      className="flex-1 rounded-full bg-primary px-4 py-2 text-center text-xs font-semibold text-primary-foreground"
                    >
                      {v.approveBtn}
                    </motion.span>
                    <span className="flex-1 rounded-full border border-hairline px-4 py-2 text-center text-xs font-medium text-ink-soft">
                      {v.reviewBtn}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {step === 4 && (
            <motion.div key="s4" {...fade} className="relative flex h-full w-full items-center justify-center">
              {/* Narrow screens: the ring would collide with the centre, so chips stack below */}
              <div className="flex flex-col items-center text-center sm:hidden">
                <motion.span
                  initial={{ scale: 0.6, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 180, damping: 14 }}
                  className="flex h-14 w-14 items-center justify-center rounded-full bg-accent text-primary shadow-lg"
                >
                  <Check className="h-7 w-7" strokeWidth={3} />
                </motion.span>
                <p className="font-display mt-3 text-base tracking-tight text-ink">{v.doneTitle}</p>
                <p className="mt-1 max-w-[230px] text-[11px] leading-relaxed text-ink-soft">{v.doneBody}</p>
                <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                  {ERP_LOGOS.map((l) => (
                    <span
                      key={l.alt}
                      className="flex h-9 w-[72px] items-center justify-center rounded-lg border border-hairline bg-background px-2 shadow-sm"
                    >
                      <img
                        src={l.src}
                        alt={l.alt}
                        loading="lazy"
                        className="max-h-5 max-w-full w-auto object-contain"
                      />
                    </span>
                  ))}
                </div>
              </div>

              <div className="hidden h-full w-full items-center justify-center sm:flex">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
                  className="absolute h-[320px] w-[320px] rounded-full border border-dashed border-hairline"
                >
                  {ERP_LOGOS.map((l, i) => {
                    const angle = (360 / ERP_LOGOS.length) * i;
                    return (
                      <div
                        key={l.alt}
                        className="absolute left-1/2 top-1/2 h-16 w-24"
                        style={{
                          marginLeft: -48,
                          marginTop: -32,
                          transform: `rotate(${angle}deg) translateY(-160px) rotate(${-angle}deg)`,
                        }}
                      >
                        <motion.div
                          animate={{ rotate: -360 }}
                          transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
                          className="flex h-16 w-24 items-center justify-center rounded-xl border border-hairline bg-background px-3 py-2 shadow-md"
                        >
                          <img
                            src={l.src}
                            alt={l.alt}
                            loading="lazy"
                            className="max-h-9 max-w-full w-auto object-contain"
                          />
                        </motion.div>
                      </div>
                    );
                  })}
                </motion.div>

                <div className="relative z-10 flex flex-col items-center text-center">
                  <motion.span
                    initial={{ scale: 0.6, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 180, damping: 14 }}
                    className="flex h-16 w-16 items-center justify-center rounded-full bg-accent text-primary shadow-lg"
                  >
                    <Check className="h-8 w-8" strokeWidth={3} />
                  </motion.span>
                  <p className="font-display mt-4 text-lg tracking-tight text-ink">{v.doneTitle}</p>
                  <p className="mt-1 max-w-[220px] text-xs leading-relaxed text-ink-soft">{v.doneBody}</p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
